import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../constants';
import SnusProductDetailContent from './SnusProductDetailContent';
import { SNUS_PROJECT_KEY, decodeSnusSkuKey } from './snusProductUtils';

const apiBase = `${API_URL}/sweden-snus-admin`;

/**
 * Full-page product detail for Sweden snus SKUs (separate from JTI ProductDetailView).
 */
export default function SnusProductDetailView() {
  const { skuKey: encodedSkuKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const skuKey = decodeSnusSkuKey(encodedSkuKey);
  const country = searchParams.get('country') || 'Sweden';
  const brand = searchParams.get('brand') || '';
  const category = searchParams.get('category') || '';
  const week = searchParams.get('week') || '';
  const retailerTable = searchParams.get('retailer_table') || '';

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!skuKey || !brand || !category) {
      setError('Missing brand or category in URL. Open this page from shelf health.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = { sku_key: skuKey, brand, country, category };
      if (week) params.week = week;
      if (retailerTable) params.retailer_table = retailerTable;
      const path = retailerTable ? `${apiBase}/retailer/sku-detail` : `${apiBase}/sku-detail`;
      const res = await axios.get(path, {
        params,
        withCredentials: true,
      });
      setDetail(res.data);
    } catch (e) {
      setDetail(null);
      setError(e.response?.data?.detail || e.message || 'Failed to load product detail');
    } finally {
      setLoading(false);
    }
  }, [skuKey, brand, country, category, week, retailerTable]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBack />}
        component={RouterLink}
        to={`/${SNUS_PROJECT_KEY}/data-insights`}
        sx={{ mb: 2 }}
      >
        Back to shelf health
      </Button>

      <Typography variant="overline" color="text.secondary">
        {retailerTable ? `Retailer ${retailerTable}` : 'Sweden snus'} · product detail
      </Typography>

      {loading && !detail && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Paper variant="outlined" sx={{ p: 3, mt: 1 }}>
          <SnusProductDetailContent detail={detail} loading={loading} error={error} />
        </Paper>
      )}

      {error && !loading && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Ensure the week datasets are downloaded, then reopen from the SKU table.
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="text" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </Box>
    </Box>
  );
}
